import React, { useState, useEffect } from 'react';
import { getSignedUrl, api } from '../lib/supabase';

// Simple in-memory cache for signed URLs
const urlCache = new Map<string, { signedUrl: string; expiresAt: number }>();

// Check if a string is a storage path (not a full URL)
const isStoragePath = (str: string): boolean => {
  return !str.startsWith('http') && !str.startsWith('//') && str.includes('/');
};

export function useSignedUrl(originalUrl: string | null | undefined, bucket: string = 'onboarding-documents') {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!originalUrl) {
      setSignedUrl(null);
      return;
    }

    // Check cache first
    const cacheKey = `${bucket}:${originalUrl}`;
    const cached = urlCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      setSignedUrl(cached.signedUrl);
      return;
    }

    // If it's a storage path (not a full URL), use api.getSignedUrl with bucket
    if (isStoragePath(originalUrl)) {
      setLoading(true);
      setError(null);

      api.getSignedUrl(bucket, originalUrl)
        .then((url) => {
          if (url) {
            urlCache.set(cacheKey, {
              signedUrl: url,
              expiresAt: Date.now() + 50 * 60 * 1000,
            });
            setSignedUrl(url);
          } else {
            setError('Failed to get signed URL');
          }
        })
        .catch((err) => {
          setError(err.message || 'Failed to get signed URL');
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    // If not a supabase storage URL, use as-is (external URLs like GCS)
    if (!originalUrl.includes('supabase.co/storage')) {
      setSignedUrl(originalUrl);
      return;
    }

    // Note: Even URLs with /object/public/ need signed URLs if the bucket is private
    // The onboarding-documents bucket is private, so always get signed URLs

    // Fetch signed URL for full supabase URLs
    setLoading(true);
    setError(null);

    getSignedUrl(originalUrl)
      .then((url) => {
        if (url) {
          // Cache for 50 minutes (signed URLs expire in 60 minutes)
          urlCache.set(cacheKey, {
            signedUrl: url,
            expiresAt: Date.now() + 50 * 60 * 1000,
          });
          setSignedUrl(url);
        } else {
          setError('Failed to get signed URL');
        }
      })
      .catch((err) => {
        setError(err.message || 'Failed to get signed URL');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [originalUrl, bucket]);

  return { signedUrl, loading, error };
}

// Component for displaying images with signed URLs
export function SignedImage({
  src,
  alt,
  className,
  fallback,
  bucket = 'onboarding-documents',
}: {
  src: string | null | undefined;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  bucket?: string;
}) {
  const { signedUrl, loading, error } = useSignedUrl(src, bucket);

  if (!src || error) {
    return fallback ? <>{fallback}</> : null;
  }

  if (loading || !signedUrl) {
    return (
      <div className={`animate-pulse bg-slate-200 ${className || ''}`} />
    );
  }

  return <img src={signedUrl} alt={alt} className={className} />;
}
